import { memo, useCallback, useState } from 'react';
import { useInventory } from '../../contexts/InventoryContext';
import { COLUMNS, cellStyle } from '../../contexts/inventoryColumns';
import DocumentDetailPanel from './DocumentDetailPanel';
import { api } from '../../api';

function isPendingReview(doc) {
  if (doc.classification_source !== 'human_manual') return false;
  const history = doc.classification_history || [];
  if (history.length === 0) return true;
  return history[history.length - 1]?.source !== 'ack';
}

/**
 * DocumentRow — a single document in the inventory list.
 *
 * Cell contents are now driven by `visibleColumns` from the registry.
 * The row itself owns chrome (checkbox, expand, selection state, detail
 * panel) and supplies a `ctx` object to each cell renderer.
 */
function DocumentRow({
  doc,
  visibleColumns,
  caseId,
  folderPaths,
  onDocChange,
  onShowDetail,
  onShowViewer,
  onCheckboxToggle,
  hashCountMap = {},
  cutChildCounts = null,
  isDimmed = false,
  onUseAsMatch = null,
  matchBusy = false,
  measureRef,
  dataIndex,
  style,
}) {
  const { dispatch, ...state } = useInventory();
  const isSelected = state.selected.has(doc.id);
  const isExpanded = state.expandedDocs.has(doc.id);
  const [ackBusy, setAckBusy] = useState(false);
  const pendingReview = isPendingReview(doc);

  const handleAck = useCallback(async (e) => {
    e.stopPropagation();
    if (ackBusy) return;
    setAckBusy(true);
    try {
      await api.post(`/documents/${doc.id}/correction-ack`);
      onDocChange?.();
    } catch (err) {
      console.error('correction-ack failed', err);
    } finally {
      setAckBusy(false);
    }
  }, [ackBusy, doc.id, onDocChange]);

  const ctx = {
    dispatch,
    hashCountMap,
    cutChildCounts,
    onAck: handleAck,
    ackBusy,
    pendingReview,
  };

  return (
    <div
      ref={measureRef}
      data-index={dataIndex}
      style={style}
      className={`inv-row ${isSelected ? 'inv-row--selected' : ''} ${isDimmed ? 'inv-row--dimmed' : ''} ${doc.is_compilation_container ? 'inv-row--container' : ''}`}
      title={isDimmed ? 'Already matched to another checklist item' : undefined}
    >
      <div
        className="inv-row-main"
        onClick={() => dispatch({ type: 'TOGGLE_DOC_EXPAND', id: doc.id })}
      >
        {/* Custom checkbox: the checkmark is a pure function of isSelected
            (React state), so it can't desync from the native control's
            internal checked state. Click toggles selection; stopPropagation
            keeps the click off the row's expand handler. */}
        <button
          type="button"
          role="checkbox"
          aria-checked={isSelected}
          aria-label={isSelected ? 'Deselect document' : 'Select document'}
          className={`inv-row-check ${isSelected ? 'inv-row-check--on' : ''}`}
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            if (onCheckboxToggle) {
              onCheckboxToggle(doc.id, e.shiftKey);
            } else {
              dispatch({ type: 'SELECT_DOC', id: doc.id });
            }
          }}
        >
          {isSelected && <span className="inv-row-check-mark" aria-hidden="true">✓</span>}
        </button>

        {visibleColumns.map((id) => {
          const col = COLUMNS[id];
          if (!col) return null;
          return (
            <div
              key={id}
              className={`inv-row-cell inv-row-cell--${id}`}
              style={cellStyle(col, state.columnWidths?.[id])}
            >
              {col.renderCell(doc, ctx)}
            </div>
          );
        })}

        {onUseAsMatch && (
          <button
            type="button"
            className="btn btn-sm btn-primary inv-row-use-match"
            onClick={(e) => {
              e.stopPropagation();
              onUseAsMatch(doc);
            }}
            disabled={matchBusy}
            title="Use this document as the match for the current checklist item"
          >
            📎 Use as match
          </button>
        )}

        <span className={`inv-row-chevron ${isExpanded ? 'inv-row-chevron--open' : ''}`}>
          ▸
        </span>
      </div>

      {isExpanded && (
        <DocumentDetailPanel
          doc={doc}
          caseId={caseId}
          folderPaths={folderPaths}
          onDocChange={onDocChange}
          onShowDetail={onShowDetail}
          onShowViewer={onShowViewer}
        />
      )}
    </div>
  );
}

export default memo(DocumentRow);
